import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import React from 'react';
import { Adornment, StyledOutlinedInput } from './styles';

function SSRInitialFilled(_: BaseNumberField.Root.Props) {
  return null;
}
SSRInitialFilled.muiName = 'Input';

function NumberField({
  id: idProp,
  label,
  error,
  size = 'medium',
  helperText,
  ...other
}: BaseNumberField.Root.Props & {
  label?: React.ReactNode;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: React.ReactNode;
}) {
  let id = React.useId();

  if (idProp) {
    id = idProp;
  }

  return (
    <BaseNumberField.Root
      {...other}
      render={(props, state) => {
        // Control label shrink state if component is focused or not empty
        const shrink = state.focused || state.inputValue !== '';

        return (
          <FormControl
            size={size}
            ref={props.ref}
            disabled={state.disabled}
            required={state.required}
            error={error}
            variant="outlined"
          >
            <SSRInitialFilled {...other} />
            <InputLabel htmlFor={id} shrink={shrink}>
              {' '}
              {label}
            </InputLabel>
            <BaseNumberField.Input
              id={id}
              render={(inputProps, inputState) => (
                <StyledOutlinedInput
                  aria-describedby={`${id}-helper-text`}
                  label={label}
                  notched={shrink}
                  inputRef={inputProps.ref}
                  value={inputState.value}
                  onBlur={inputProps.onBlur}
                  onChange={inputProps.onChange}
                  onKeyUp={inputProps.onKeyUp}
                  onKeyDown={inputProps.onKeyDown}
                  onFocus={inputProps.onFocus}
                  slotProps={{
                    input: inputProps,
                  }}
                  endAdornment={
                    <Adornment position="end">
                      <BaseNumberField.Increment
                        render={
                          <IconButton size={size} aria-label="Increase" />
                        }
                      >
                        <ChevronUpIcon
                          size={size === 'small' ? 16 : 20}
                          style={{ transform: 'translateY(2px)' }}
                        />
                      </BaseNumberField.Increment>
                      <BaseNumberField.Decrement
                        render={
                          <IconButton size={size} aria-label="Decrease" />
                        }
                      >
                        <ChevronDownIcon
                          size={size === 'small' ? 16 : 20}
                          style={{ transform: 'translateY(-2px)' }}
                        />
                      </BaseNumberField.Decrement>
                    </Adornment>
                  }
                  sx={{ pr: 0 }}
                />
              )}
            />
            <FormHelperText
              id={`${id}-helper-text`}
              sx={{ ml: 0, '&:empty': { mt: 0 } }}
            >
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}

export default NumberField;
